import { useState } from 'react';
import { Loader2, Mic, MicOff } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@clarix/ui';
import { cn } from '@clarix/ui/lib/cn';
import { ConfirmDialog } from '@clarix/ui-shared';
import { CALENDAR_PATH, QM_RECOVERY } from './constants';
import { QM_COPY, UNAVAILABLE_HINT } from './copy';
import {
  orphanedRecording,
  useQuickMeeting,
  type OrphanClock,
  type QuickMeetingContextValue,
} from './QuickMeetingContext';
import { RecordingPill, RemoteRecordingPill } from './RecordingPill';
import { LocalCopiesList, OrphanedRecordingRow, RecordingsStatusList } from './RecordingsStatusList';
import { sidecars, type SessionSidecar } from './browser';
import { StartRecordingForm } from './StartRecordingForm';
import type { RecordingStatusDTO } from './types';
import { useNow } from './useNow';
import { usePageVisible } from './browserSignals';

/** A take ended with its audio still on this device (synchronous: localStorage only). */
const hasLocalCopies = (scope: string | null) => !!scope && sidecars.list(scope).some((s) => s.ended);

/** The mic's corner dot: what needs the caller's attention first. */
function attentionDot(qm: QuickMeetingContextValue, status: RecordingStatusDTO, clock: OrphanClock): string | null {
  if (qm.local.phase === 'save_failed' || status.recoverable.length > 0) return 'bg-warning';
  if (orphanedRecording(status, qm.local, qm.remote, clock)) return 'bg-warning';
  if (hasLocalCopies(qm.scope)) return 'bg-warning';
  if (status.unseen.some((r) => r.status === 'failed')) return 'bg-destructive';
  if (status.unseen.some((r) => r.status === 'ready')) return 'bg-primary';
  if (status.unseen.length > 0) return 'bg-muted-foreground';
  return null;
}

interface MicPanelProps {
  qm: QuickMeetingContextValue;
  status: RecordingStatusDTO;
  clock: OrphanClock;
  onDiscard: (id: string) => void;
  onDeleteLocal: (entry: SessionSidecar) => void;
}

function MicPanel({ qm, status, clock, onDiscard, onDeleteLocal }: MicPanelProps) {
  const reason = status.quick_meeting.reason;
  // Read once per open: the full notice shows until the caller starts under this version.
  const orphan = orphanedRecording(status, qm.local, qm.remote, clock);

  return (
    <div className="flex max-h-[min(70vh,560px)] flex-col">
      <div className="relative overflow-y-auto">
        <div className="flex flex-col gap-3 p-3">
          <h2 className="text-foreground text-xl font-semibold">{QM_COPY.popoverTitle}</h2>
          {orphan ? (
            <OrphanedRecordingRow qm={qm} recording={orphan} onDiscard={onDiscard} />
          ) : qm.canStart ? (
            <StartRecordingForm
              phase={qm.local.phase}
              error={qm.local.error}
              minutesLeft={status.quick_meeting.minutes_left_today ?? null}
              mic={qm.mic}
              onStart={(input) => void qm.start(input)}
            />
          ) : (
            <div className="flex flex-col gap-1" role="status">
              <p className="text-md text-foreground flex items-start gap-1.5">
                <MicOff className="text-muted-foreground mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                {QM_COPY.unavailable[reason ?? 'NO_PERMISSION']}
              </p>
              {reason && UNAVAILABLE_HINT[reason] && (
                <p className="text-muted-foreground pl-5 text-sm">{UNAVAILABLE_HINT[reason]}</p>
              )}
            </div>
          )}
        </div>
        <RecordingsStatusList qm={qm} now={clock.now} onDiscard={onDiscard} />
        <LocalCopiesList qm={qm} onDelete={onDeleteLocal} />
      </div>
      <div className="border-border border-t p-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full justify-center"
          onClick={() => qm.openCalendarPath(CALENDAR_PATH.RECORDINGS)}
        >
          {QM_COPY.allRecordings}
        </Button>
      </div>
    </div>
  );
}

function MicButton({ qm, status }: { qm: QuickMeetingContextValue; status: RecordingStatusDTO }) {
  const [open, setOpen] = useState(false);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [confirmLocal, setConfirmLocal] = useState<SessionSidecar | null>(null);
  const { phase } = qm.local;
  const busy = phase === 'requesting_mic' || phase === 'starting';
  const denied = phase === 'denied';
  const unavailable = !qm.canStart;
  const processing = status.processing.length > 0;
  const now = useNow(QM_RECOVERY.CLOCK_TICK_MS, status.active != null || processing);
  const visible = usePageVisible();
  const clock: OrphanClock = { now, fetchedAt: qm.statusFetchedAt, visible };
  const dot = attentionDot(qm, status, clock);
  const label = [
    QM_COPY.micLabel,
    unavailable ? `, ${QM_COPY.micUnavailable}` : '',
    processing ? `, ${status.processing.length} processing` : '',
  ].join('');

  return (
    <>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) qm.clearError();
        }}
      >
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={label}
            title={QM_COPY.micLabel}
            className={cn(
              'relative h-8 w-8 shrink-0',
              denied ? 'text-warning-fg' : 'text-muted-foreground hover:text-foreground',
              unavailable && 'opacity-70',
            )}
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            ) : denied || unavailable ? (
              <MicOff className="h-5 w-5" strokeWidth={1.5} aria-hidden />
            ) : (
              <Mic className="h-5 w-5" strokeWidth={1.5} aria-hidden />
            )}
            {processing && !busy && (
              <Loader2
                className="text-primary bg-background absolute -bottom-0.5 -right-0.5 h-3 w-3 animate-spin rounded-full"
                aria-hidden
              />
            )}
            {dot && !processing && (
              <span
                className={cn('ring-background absolute right-1 top-1 h-2 w-2 rounded-full ring-2', dot)}
                aria-hidden
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-0" lockScroll={false}>
          <MicPanel qm={qm} status={status} clock={clock} onDiscard={setConfirmId} onDeleteLocal={setConfirmLocal} />
        </PopoverContent>
      </Popover>
      <ConfirmDialog
        open={confirmId != null}
        onOpenChange={(o) => {
          if (!o) setConfirmId(null);
        }}
        title={QM_COPY.discardTitle}
        description={QM_COPY.discardBody}
        confirmLabel={QM_COPY.discardConfirm}
        variant="destructive"
        loading={confirmId != null && qm.rows.pendingId === confirmId}
        onConfirm={async () => {
          if (!confirmId) return;
          await qm.rows.discard(confirmId);
          setConfirmId(null);
        }}
      />
      <ConfirmDialog
        open={confirmLocal != null}
        onOpenChange={(o) => {
          if (!o) setConfirmLocal(null);
        }}
        title={QM_COPY.deleteLocalTitle}
        description={QM_COPY.deleteLocalBody}
        confirmLabel={QM_COPY.deleteLocalConfirm}
        variant="destructive"
        loading={confirmLocal != null && qm.rows.pendingId === confirmLocal.recordingId}
        onConfirm={async () => {
          if (!confirmLocal) return;
          await qm.rows.deleteLocal(confirmLocal);
          setConfirmLocal(null);
        }}
      />
    </>
  );
}

/** The mic, or this tab's recording pill, or another tab's: never hidden once it applies. */
export function QuickMeetingMic() {
  const qm = useQuickMeeting();
  if (!qm || !qm.access.ready) return null;
  const { phase } = qm.local;
  if (phase === 'recording' || phase === 'paused' || phase === 'saving' || phase === 'save_failed' || phase === 'no_audio') {
    return <RecordingPill variant="bar" />;
  }
  if (qm.remote) return <RemoteRecordingPill variant="bar" />;
  const status = qm.status;
  // Fails closed while the status loads; without the key it shows only for the caller's own rows.
  if (!status) return null;
  const hasRows =
    status.processing.length + status.unseen.length + status.recoverable.length + (status.active ? 1 : 0) > 0 ||
    hasLocalCopies(qm.scope);
  const allowed = qm.access.canRecord && status.quick_meeting.reason !== 'NO_PERMISSION';
  if (!allowed && !hasRows) return null;
  return <MicButton qm={qm} status={status} />;
}
